import { z } from 'zod';
import { idSchema, isoTimestampSchema } from '../domain/primitives.js';
import { authSourceSchema, userSchema } from '../domain/user.js';
/**
 * Auth wire contracts for `/api/v1/auth`.
 *
 * Request bodies reuse `registerUserInputSchema` and `loginInputSchema` from
 * `domain/user.ts` - they are not redeclared here.
 */
export const accessTokenSchema = z.object({
    /** The JWT, to be sent as `Authorization: Bearer <token>`. */
    token: z.string().min(1),
    /**
     * When the token stops being accepted, as ISO-8601. Exposed so a client can
     * pre-empt expiry without decoding the JWT itself.
     */
    expiresAt: isoTimestampSchema,
});
/**
 * Response body of `POST /auth/register` and `POST /auth/login`.
 *
 * `user` is the standard `User` DTO, which by construction carries no password
 * hash - see the note on `userSchema`.
 */
export const authResponseSchema = z.object({
    user: userSchema,
    accessToken: accessTokenSchema,
});
/** Response body of `GET /auth/me`. */
export const meResponseSchema = userSchema;
/**
 * Response body of `GET /auth/options` - public, so the sign-in page can say
 * whether a directory account works there before anyone has signed in.
 */
export const authOptionsSchema = z.object({
    /** Directory (LDAP / Active Directory) sign-in is switched on. */
    directoryEnabled: z.boolean(),
    /** What the administrator calls the directory, e.g. "Contoso Active Directory". */
    directoryName: z.string().nullable(),
});
// ---- Sessions ----------------------------------------------------------------
/**
 * One sign-in: a browser or device that holds a session.
 *
 * Sessions are server-side records, so each one can be listed and ended on its
 * own - by the person (`/auth/sessions`) or by an administrator. The session
 * secret itself never appears here; only facts about it.
 */
export const userSessionSchema = z.object({
    id: idSchema,
    createdAt: isoTimestampSchema,
    /** Last time this session fetched a fresh access token - roughly, last seen. */
    lastUsedAt: isoTimestampSchema,
    /** When it ends at the latest under the current lifetime setting. */
    expiresAt: isoTimestampSchema,
    /** The browser's user agent string as it signed in, trimmed. */
    userAgent: z.string().nullable(),
    ip: z.string().nullable(),
    method: authSourceSchema,
    /** This is the session making the request. Always false in the admin view. */
    current: z.boolean(),
});
//# sourceMappingURL=auth.js.map